import { useState, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { Button, FormError, Modal, type ButtonIcon } from '../../../core/index.js'
import type { ChangeRequestView } from '../../types.js'
import { useChangeStep } from './useChangeStep.js'

const REASON_MAX_LENGTH = 2000

interface StepDialogProps {
  eyebrow: string
  title: string
  subtitle?: ReactNode
  children: ReactNode
  confirmLabel: string
  confirmIcon: ButtonIcon
  /** False while the form is not yet complete. */
  canConfirm: boolean
  /** The call this step makes; its answer replaces the change on screen. */
  run: () => Promise<ChangeRequestView>
  onClose: () => void
}

/** A dialog for one step of a change: what it binds, the form, and Confirm; the API's refusal is shown in it, in the API's words. */
export function StepDialog({ eyebrow, title, subtitle, children, confirmLabel, confirmIcon, canConfirm, run, onClose }: StepDialogProps) {
  const step = useChangeStep(() => onClose())
  return (
    <Modal
      eyebrow={eyebrow}
      title={title}
      subtitle={subtitle}
      canClose={!step.isPending}
      onClose={onClose}
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="mr-auto">
            <FormError error={step.error} fallback="That step could not be taken." />
          </div>
          <Button icon={X} variant="secondary" size="sm" onClick={onClose} disabled={step.isPending}>
            Cancel
          </Button>
          <Button icon={confirmIcon} size="sm" onClick={() => step.mutate(run)} disabled={!canConfirm || step.isPending}>
            {step.isPending ? 'Working…' : confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3 px-6 py-4">{children}</div>
    </Modal>
  )
}

interface ReasonDialogProps {
  title: string
  /** The question the text answers, above the box. */
  label: string
  placeholder?: string
  confirmLabel: string
  confirmIcon: ButtonIcon
  /** Anything to show above the box: what the step binds. */
  children?: ReactNode
  run: (reason: string) => Promise<ChangeRequestView>
  onClose: () => void
}

/** A step that needs a reason or notes: Return, Send back, Reject, Cancel. */
export function ReasonDialog({ title, label, placeholder, confirmLabel, confirmIcon, children, run, onClose }: ReasonDialogProps) {
  const [reason, setReason] = useState('')
  const trimmed = reason.trim()
  return (
    <StepDialog eyebrow="Change request" title={title} confirmLabel={confirmLabel} confirmIcon={confirmIcon} canConfirm={trimmed !== ''} run={() => run(trimmed)} onClose={onClose}>
      {children}
      <label className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid">
        {label}
        <textarea
          value={reason}
          maxLength={REASON_MAX_LENGTH}
          onChange={(event) => setReason(event.target.value)}
          rows={4}
          autoFocus
          placeholder={placeholder}
          className="mt-1 w-full border border-marine/30 rounded px-2 py-1 text-xs font-normal normal-case tracking-normal text-ink bg-white focus:outline-none focus:ring-2 focus:ring-coral"
        />
      </label>
    </StepDialog>
  )
}
